import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  useTheme,
  Paper,
  Pagination,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getRoomAllocationBookings, getBookingDetails, updateBooking } from '../services/api';
import BookingDetailsDrawer from '../components/booking/BookingDetailsDrawer';
import BookingOpsBookingCard from '../components/booking/BookingOpsBookingCard';
import RoomAllocationDrawer from '../components/booking/RoomAllocationDrawer';
import DashboardHeader from '../components/common/DashboardHeader';
import EmptyStateCard from '../components/common/EmptyStateCard';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getDashboardPageShellSx, getDashboardPaperSx } from '../utils/dashboardSurfaceSx';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import { getBookingOpsGridItemSx } from '../utils/bookingOperationsMobileLayout';
import { getHotelDateFormat } from '../utils/dateUtils';
import { patchBookingInList } from '../utils/bookingListPatch';
import { shouldCheckoutOldRoomSegment } from '../utils/roomChangeSegment';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const RoomAllocationPage = () => {
  const theme = useTheme();
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [selectedDate, setSelectedDate] = useState(moment());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [allocationDrawerOpen, setAllocationDrawerOpen] = useState(false);
  const [allocationDrawerMode, setAllocationDrawerMode] = useState('allocate');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // 3 rows × 4 cards per row
  const fetchAbortController = useRef(null);
  const debounceTimer = useRef(null);

  // Debounced fetch to prevent multiple rapid calls
  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Cancel any ongoing fetch
    if (fetchAbortController.current) {
      fetchAbortController.current.abort();
    }

    // Debounce the fetch
    debounceTimer.current = setTimeout(() => {
      setHasFetched(false); // Reset fetch status when date changes
      fetchBookings();
      setCurrentPage(1); // Reset to first page when date changes
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (fetchAbortController.current) {
        fetchAbortController.current.abort();
      }
    };
  }, [selectedDate]);

  const fetchBookings = useCallback(async () => {
    // Create new AbortController for this fetch
    fetchAbortController.current = new AbortController();
    const signal = fetchAbortController.current.signal;

    setLoading(true);
    setError(null);
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const data = await getRoomAllocationBookings(dateStr);
      if (signal.aborted) return;
      setBookings(data.bookings || []);
      setHasFetched(true);
    } catch (err) {
      if (!err.name || err.name !== 'AbortError') {setError('Failed to load room allocation bookings. Please try again.');
      }
    } finally {
      if (!fetchAbortController.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [selectedDate]);

  const normalizeBookingId = (value) => {
    const parsedId = Number(value);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  };

  /** Update one card after drawer payment — no full-list loading flash. */
  const refreshBookingCard = useCallback(async (bookingIdArg) => {
    const id = normalizeBookingId(bookingIdArg ?? selectedBookingId);
    if (!id) return;
    try {
      const details = await getBookingDetails(id);
      setBookings((prev) => patchBookingInList(prev, id, details, normalizeBookingId));
    } catch {
      // Keep existing cards; avoid forcing a full-list reload.
    }
  }, [selectedBookingId]);

  const handleDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const handlePrevClick = () => {
    const newDate = selectedDate.clone().subtract(1, 'days');
    setSelectedDate(newDate);
  };

  const handleNextClick = () => {
    const newDate = selectedDate.clone().add(1, 'days');
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(moment());
  };

  const handleBookingClick = (booking) => {
    const status = String(booking?.status || '').toLowerCase();
    const isCheckedIn =
      status === 'checked_in' ||
      status === 'partial_checkin' ||
      status === 'partial_checkout';
    const checkInDate = booking?.check_in_date ? moment(booking.check_in_date) : null;
    const canChangeRoom =
      isCheckedIn &&
      checkInDate &&
      checkInDate.isValid() &&
      moment().startOf('day').isSameOrAfter(checkInDate.clone().startOf('day').add(1, 'day'));
    setAllocationDrawerMode(canChangeRoom ? 'changeRoom' : 'allocate');
    setSelectedBookingId(booking?.id || null);
    setAllocationDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setAllocationDrawerOpen(false);
    setSelectedBookingId(null);
    setAllocationDrawerMode('allocate');
  };

  const handleAssignSuccess = (booking) => {
    // Refresh bookings list after successful room assignment
    fetchBookings();
    setAllocationDrawerOpen(false);
    setSelectedBookingId(null);
  };

  const handleChangeRoomSuccess = async ({
    newRoomId,
    changeFromDate: fromStr,
    group,
    targetItemId,
    targetRoomTypeId: targetRoomTypeIdInput,
    targetSequence: targetSequenceInput,
    streamAssignments,
  }) => {
    if (!selectedBookingId) return;

    const effectiveFromDate = fromStr || moment().format('YYYY-MM-DD');
    const changedAtIso = moment().toISOString();
    try {
      const detail = await getBookingDetails(selectedBookingId);
      const detailItems = detail?.booking_items || detail?.items || [];
      if (!detailItems.length) {
        setError('Unable to change room: booking items not found.');
        return;
      }

      const groupItemIds = new Set((group?.items || []).map((i) => i.id).filter(Boolean));
      const targetFromGroup = groupItemIds.size
        ? detailItems.find((item) => groupItemIds.has(item.id))
        : null;
      const targetFromId = targetItemId
        ? detailItems.find((item) => item.id === targetItemId)
        : null;
      const normalizeDateStr = (value) => {
        if (!value) return '';
        return moment.isMoment(value) ? value.format('YYYY-MM-DD') : String(value).slice(0, 10);
      };
      const sortedDetailItems = [...detailItems].sort((a, b) => {
        const aDate = normalizeDateStr(a?.date);
        const bDate = normalizeDateStr(b?.date);
        if (aDate !== bDate) return aDate.localeCompare(bDate);
        return (a?.id || 0) - (b?.id || 0);
      });
      const pickPreferredTargetItem = (items) =>
        items.find((it) => it?.checkout !== true && normalizeDateStr(it?.date) >= effectiveFromDate) ||
        items.find((it) => it?.checkout !== true) ||
        items.find((it) => normalizeDateStr(it?.date) >= effectiveFromDate) ||
        items[0] ||
        null;

      const normalizedAssignments = Array.isArray(streamAssignments)
        ? streamAssignments.filter(Boolean)
        : [];

      let nextItems = [...detailItems];
      if (normalizedAssignments.length > 0) {
        normalizedAssignments.forEach((assignment) => {
          const targetRoomTypeId = Number(assignment?.targetRoomTypeId ?? 0) || null;
          const targetSequence = Number(assignment?.targetSequence ?? 1) || 1;
          const assignmentRoomId = assignment?.newRoomId ?? null;
          if (!targetRoomTypeId) return;

          nextItems = nextItems.map((item) => {
            const itemRoomTypeId = item?.room_type_id ?? item?.room_type?.id ?? null;
            const itemSequence = item?.room_sequence ?? 1;
            const isTargetSequence =
              String(itemRoomTypeId) === String(targetRoomTypeId) &&
              Number(itemSequence) === Number(targetSequence);
            if (!isTargetSequence) return item;
            if (item?.checkout === true) return item;

            const streamItems = nextItems.filter((candidate) => {
              const candidateTypeId = candidate?.room_type_id ?? candidate?.room_type?.id ?? null;
              const candidateSeq = candidate?.room_sequence ?? 1;
              return (
                String(candidateTypeId) === String(targetRoomTypeId)
                && Number(candidateSeq) === Number(targetSequence)
              );
            });
            if (shouldCheckoutOldRoomSegment(item, effectiveFromDate, streamItems)) {
              return { ...item, checkout: true, segment_ended_at: changedAtIso };
            }
            return {
              ...item,
              allocated_room_id: assignmentRoomId,
              checkout: false,
              segment_started_at: changedAtIso,
              room_changed_at: changedAtIso,
            };
          });
        });
      } else {
        const payloadRoomTypeId = targetRoomTypeIdInput != null ? Number(targetRoomTypeIdInput) : null;
        const payloadSequence = targetSequenceInput != null ? Number(targetSequenceInput) : null;
        const payloadStreamItems =
          payloadRoomTypeId != null && payloadSequence != null
            ? sortedDetailItems.filter((it) => {
                const itType = Number(it?.room_type_id ?? it?.room_type?.id ?? 0);
                const itSeq = Number(it?.room_sequence ?? 1);
                return itType === payloadRoomTypeId && itSeq === payloadSequence;
              })
            : [];

        const fallbackTarget = pickPreferredTargetItem(
          payloadStreamItems.length ? payloadStreamItems : sortedDetailItems
        );
        const initialTarget = targetFromGroup || targetFromId || fallbackTarget || sortedDetailItems[0];

        const resolvedRoomTypeId =
          payloadRoomTypeId ?? (Number(initialTarget?.room_type_id ?? initialTarget?.room_type?.id ?? 0) || null);
        const resolvedSequence =
          payloadSequence ?? (Number(initialTarget?.room_sequence ?? 1) || 1);
        const resolvedStreamItems = sortedDetailItems.filter((it) => {
          const itType = Number(it?.room_type_id ?? it?.room_type?.id ?? 0);
          const itSeq = Number(it?.room_sequence ?? 1);
          return (
            resolvedRoomTypeId != null &&
            itType === Number(resolvedRoomTypeId) &&
            itSeq === Number(resolvedSequence)
          );
        });
        const targetItem = pickPreferredTargetItem(
          resolvedStreamItems.length ? resolvedStreamItems : [initialTarget].filter(Boolean)
        ) || fallbackTarget || sortedDetailItems[0];

        const targetRoomTypeId = resolvedRoomTypeId ?? (targetItem?.room_type_id ?? targetItem?.room_type?.id ?? null);
        const targetSequence = resolvedSequence ?? (targetItem?.room_sequence ?? 1);
        if (!targetRoomTypeId) {
          setError('Unable to change room: target room type not found.');
          return;
        }

        nextItems = detailItems.map((item) => {
          const itemRoomTypeId = item?.room_type_id ?? item?.room_type?.id ?? null;
          const itemSequence = item?.room_sequence ?? 1;
          const isTargetSequence =
            String(itemRoomTypeId) === String(targetRoomTypeId) &&
            Number(itemSequence) === Number(targetSequence);
          if (!isTargetSequence) return item;
          if (item?.checkout === true) return item;

          const streamItems = detailItems.filter((candidate) => {
            const candidateTypeId = candidate?.room_type_id ?? candidate?.room_type?.id ?? null;
            const candidateSeq = candidate?.room_sequence ?? 1;
            return (
              String(candidateTypeId) === String(targetRoomTypeId)
              && Number(candidateSeq) === Number(targetSequence)
            );
          });
          if (shouldCheckoutOldRoomSegment(item, effectiveFromDate, streamItems)) {
            return { ...item, checkout: true, segment_ended_at: changedAtIso };
          }
          return {
            ...item,
            allocated_room_id: newRoomId || null,
            checkout: false,
            segment_started_at: changedAtIso,
            room_changed_at: changedAtIso,
          };
        });
      }

      const updateData = {
        booking_items: nextItems.map((item) => ({
          id: item.id,
          price: parseFloat(item.price) || 0,
          rate_plan_id: item.rate_plan_id || item.rate_plan?.id || null,
          allocated_room_id: item.allocated_room_id || item.allocated_room?.id || null,
          room_type_id: item.room_type_id || item.room_type?.id || null,
          room_sequence: item.room_sequence ?? 1,
          checkout: item.checkout ?? false,
          adults: item.adults,
          children: item.children,
          ...(item.segment_started_at ? { segment_started_at: item.segment_started_at } : {}),
          ...(item.segment_ended_at ? { segment_ended_at: item.segment_ended_at } : {}),
          ...(item.room_changed_at ? { room_changed_at: item.room_changed_at } : {}),
        })),
      };

      await updateBooking(selectedBookingId, updateData);
      await fetchBookings();
      setAllocationDrawerOpen(false);
      setSelectedBookingId(null);
      setAllocationDrawerMode('allocate');
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to change room.';
      setError(errorMsg);
    }
  };

  // Pagination logic
  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return bookings.slice(startIndex, startIndex + itemsPerPage);
  }, [bookings, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(bookings.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={getDashboardPageShellSx(currentTheme)}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <DashboardHeader
            title="Room Allocation"
            forceDark={isMobileShellLayout()}
            breadcrumbs={[
              { label: 'Home', path: '/dashboard/' },
              { label: 'Booking', path: '/booking/' },
              { label: 'Room Allocation' }
            ]}
            navLinks={[
              { url: '/booking/list/', label: 'Booking List' },
              { url: '/booking/checkin/', label: 'Check-In' },
              { url: '/booking/checkout/', label: 'Check-Out' },
            ]}
          />
        </Box>

        {/* Date Navigation and Total Bookings - Same style as check-in */}
        <Paper
          elevation={0}
          sx={getDashboardPaperSx(currentTheme, 'controls')}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            {/* Datepicker on the left */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
              className="dashboard-date-range"
            >
              <IconButton
                onClick={handlePrevClick}
                className="calendar-nav-button"
                sx={{
                  borderRadius: '4px',
                  width: '36px',
                  height: '36px',
                  padding: 0,
                  color: isDark ? '#ddd' : '#333',
                  '&:hover': {
                    backgroundColor: isDark ? '#38373f' : '#e0e0e0',
                  },
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Box sx={{ minWidth: '200px' }} className="calendar-date-picker">
                <DatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                  format={getHotelDateFormat()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          height: '36px',
                          fontSize: '14px',
                          '& fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                          '&:hover fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: CUSTOM_BORDER_COLOR,
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '8px 12px',
                          textAlign: 'center',
                          color: isDark ? '#ddd' : '#333',
                        },
                      },
                    },
                  }}
                />
              </Box>
              <IconButton
                onClick={handleNextClick}
                className="calendar-nav-button"
                sx={{
                  borderRadius: '4px',
                  width: '36px',
                  height: '36px',
                  padding: 0,
                  color: isDark ? '#ddd' : '#333',
                  '&:hover': {
                    backgroundColor: isDark ? '#38373f' : '#e0e0e0',
                  },
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Total Bookings on the right */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: isDark ? '#999' : '#666',
                  fontWeight: 500,
                }}
              >
                Total Bookings:
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: isDark ? '#ddd' : '#333',
                  fontWeight: 600,
                }}
              >
                {bookings.length}
              </Typography>
            </Box>
          </Box>
        </Paper>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Bookings Grid */}
      {!loading && !error && hasFetched && (
        <>
          {bookings.length === 0 ? (
            <EmptyStateCard themeMode={currentTheme}>
              No confirmed bookings found for check-in on {selectedDate.format('DD MMM YYYY')}
            </EmptyStateCard>
          ) : (
            <>
              <Grid container spacing={3}>
                {paginatedBookings.map((booking) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} sx={getBookingOpsGridItemSx()} key={booking.id}>
                    <BookingOpsBookingCard
                      booking={booking}
                      isDark={isDark}
                      accent={CUSTOM_BORDER_COLOR}
                      isMobile={false}
                      onClick={() => handleBookingClick(booking)}
                    />
                  </Grid>
                ))}
              </Grid>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: isDark ? '#ddd' : '#333',
                        borderColor: isDark ? '#333246' : '#e0e0e0',
                        '&.Mui-selected': {
                          backgroundColor: CUSTOM_BORDER_COLOR,
                          color: '#000',
                          '&:hover': {
                            backgroundColor: CUSTOM_BORDER_COLOR,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Room Allocation Drawer */}
      {selectedBookingId && (
        <RoomAllocationDrawer
          bookingId={selectedBookingId}
          mode={allocationDrawerMode}
          open={allocationDrawerOpen}
          onClose={handleDrawerClose}
          onAssignSuccess={handleAssignSuccess}
          onChangeRoomSuccess={handleChangeRoomSuccess}
          onBookingUpdated={refreshBookingCard}
        />
      )}

      {/* Booking Details Drawer (for reference, can be opened via other means) */}
      {selectedBookingId && (
        <BookingDetailsDrawer
          bookingId={selectedBookingId}
          open={drawerOpen}
          onClose={handleDrawerClose}
          onStatusUpdate={refreshBookingCard}
        />
      )}
    </Box>
    </LocalizationProvider>
  );
};

export default RoomAllocationPage;

